/**
 * PerformanceDistributionChart Component
 *
 * Horizontal bar chart showing performance distribution across the organization.
 * Categories: Excellent, Above Average, Adequate, Needs Improvement
 */
import React from 'react'
import { cn } from '@/lib/utils'
import type { PerformanceDistribution } from '@/types/radar'

interface PerformanceDistributionChartProps {
  distribution: PerformanceDistribution
  className?: string
}

const categories = [
  { key: 'excellent' as const, label: 'Excelente (4.5+)', color: 'bg-green-500' },
  { key: 'aboveAverage' as const, label: 'Acima da Média (3.5-4.5)', color: 'bg-brand-primary' },
  { key: 'adequate' as const, label: 'Adequado (2.5-3.5)', color: 'bg-yellow-500' },
  { key: 'needsImprovement' as const, label: 'Precisa Melhorar (<2.5)', color: 'bg-red-500' },
]

export function PerformanceDistributionChart({
  distribution,
  className,
}: PerformanceDistributionChartProps) {
  const total = distribution.excellent + distribution.aboveAverage + distribution.adequate + distribution.needsImprovement

  return (
    <div className={cn('space-y-3', className)}>
      {categories.map((category) => {
        const count = distribution[category.key]
        const percentage = total > 0 ? Math.round((count / total) * 100) : 0

        return (
          <div key={category.key} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-gray">{category.label}</span>
              <span className="text-white font-medium">{percentage}%</span>
            </div>
            <div className="h-2 bg-brand-dark-light rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', category.color)}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Compact inline version for cards
 */
export function PerformanceDistributionBar({
  distribution,
  className,
}: PerformanceDistributionChartProps) {
  const total = distribution.excellent + distribution.aboveAverage + distribution.adequate + distribution.needsImprovement

  if (total === 0) {
    return (
      <div className={cn('h-4 bg-brand-dark-light rounded-full', className)} />
    )
  }

  return (
    <div className={cn('h-4 flex rounded-full overflow-hidden', className)}>
      {distribution.excellent > 0 && (
        <div
          className="bg-green-500"
          style={{ width: `${(distribution.excellent / total) * 100}%` }}
          title={`Excelente: ${distribution.excellent}`}
        />
      )}
      {distribution.aboveAverage > 0 && (
        <div
          className="bg-brand-primary"
          style={{ width: `${(distribution.aboveAverage / total) * 100}%` }}
          title={`Acima da Média: ${distribution.aboveAverage}`}
        />
      )}
      {distribution.adequate > 0 && (
        <div
          className="bg-yellow-500"
          style={{ width: `${(distribution.adequate / total) * 100}%` }}
          title={`Adequado: ${distribution.adequate}`}
        />
      )}
      {distribution.needsImprovement > 0 && (
        <div
          className="bg-red-500"
          style={{ width: `${(distribution.needsImprovement / total) * 100}%` }}
          title={`Precisa Melhorar: ${distribution.needsImprovement}`}
        />
      )}
    </div>
  )
}
